import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { countOrZero, nullableBoolean, nullableString } from '../utils/wire-value-utils';

/**
 * An invitation to join an organization, as the hub's `toInvitationResponse` returns it
 * (`user-organization-invitations-controller.ts`), plus the list's `inviterName` / `inviterEmail`.
 * Mirrors hub `data-models/organization-invitation.ts`. Timestamps other than the `Base` ones stay ISO strings.
 *
 * Invitations are created from an `InvitationRequest` and never updated, so every field is read-only.
 * `role` is a `MembershipRoles` bit flag combination (Pilot and/or Pilot in Command).
 */
export class OrganizationInvitation extends Base {
  organizationId?: string;
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
  role: number;
  isAccepted: boolean;
  acceptedBy?: string | null;
  isCancelled: boolean;
  cancelledBy?: string | null;
  cancelledAt?: string | null;
  expiresAt?: string | null;
  firstReminderSentAt?: string | null;
  finalReminderSentAt?: string | null;
  lastResentAt?: string | null;
  resendCount: number;
  emailStatus?: string | null;
  emailSentAt?: string | null;
  invitedBy?: string | null;
  inviterName?: string | null;
  inviterEmail?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    // Read-only state flags and counters: missing values mean "not accepted / cancelled / resent".
    this.role = countOrZero(source['role']);
    this.isAccepted = nullableBoolean(source['isAccepted']) ?? false;
    this.isCancelled = nullableBoolean(source['isCancelled']) ?? false;
    this.resendCount = countOrZero(source['resendCount']);
    this.readInvitation(source);
    this.readHistory(source);
  }

  private readInvitation(source: Record<string, unknown>): void {
    this.organizationId = nullableString(source['organizationId']) ?? undefined;
    this.email = nullableString(source['email']) ?? undefined;
    this.firstName = nullableString(source['firstName']);
    this.lastName = nullableString(source['lastName']);
    this.acceptedBy = nullableString(source['acceptedBy']);
    this.cancelledBy = nullableString(source['cancelledBy']);
    this.cancelledAt = nullableString(source['cancelledAt']);
    this.expiresAt = nullableString(source['expiresAt']);
    this.invitedBy = nullableString(source['invitedBy']);
    this.inviterName = nullableString(source['inviterName']);
    this.inviterEmail = nullableString(source['inviterEmail']);
  }

  private readHistory(source: Record<string, unknown>): void {
    this.firstReminderSentAt = nullableString(source['firstReminderSentAt']);
    this.finalReminderSentAt = nullableString(source['finalReminderSentAt']);
    this.lastResentAt = nullableString(source['lastResentAt']);
    this.emailStatus = nullableString(source['emailStatus']);
    this.emailSentAt = nullableString(source['emailSentAt']);
  }
}
